import {io, Socket} from 'socket.io-client';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ToolEvent {
  tool: string;
  input?: any;
  result?: any;
  error?: string;
  message: string;
}

interface AIAssistantCallbacks {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onStatus?: (status: {type: string; message: string}) => void;
  onThinkingText?: (event: {text: string; message: string}) => void;
  onToolStart?: (event: ToolEvent) => void;
  onToolComplete?: (event: ToolEvent) => void;
  onToolError?: (event: ToolEvent) => void;
  onResponseStart?: () => void;
  onResponseChunk?: (chunk: string) => void;
  onResponseComplete?: (response: string, usage?: any) => void;
  onError?: (error: {message: string; error?: string}) => void;
}

class AIAssistantService {
  private socket: Socket | null = null;
  private callbacks: AIAssistantCallbacks = {};

  connect(callbacks: AIAssistantCallbacks = {}): void {
    if (this.socket?.connected) {
      return;
    }

    this.callbacks = callbacks;

    // Connect directly to backend server for WebSocket (bypassing UAT Apache)
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://13.40.177.40';
    
    console.log('Connecting to AI Assistant socket at:', `${socketUrl}/api/ai-assistant`);
    
    this.socket = io(`${socketUrl}/api/ai-assistant`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('AI Assistant connected');
      this.callbacks.onConnect?.();
    });

    this.socket.on('disconnect', () => {
      console.log('AI Assistant disconnected');
      this.callbacks.onDisconnect?.();
    });

    this.socket.on('status', (data: {type: string; message: string}) => {
      this.callbacks.onStatus?.(data);
    });

    this.socket.on('thinking_text', (event: {text: string; message: string}) => {
      this.callbacks.onThinkingText?.(event);
    });

    this.socket.on('tool_start', (event: ToolEvent) => {
      this.callbacks.onToolStart?.(event);
    });

    this.socket.on('tool_complete', (event: ToolEvent) => {
      this.callbacks.onToolComplete?.(event);
    });

    this.socket.on('tool_error', (event: ToolEvent) => {
      this.callbacks.onToolError?.(event);
    });

    this.socket.on('response_start', () => {
      this.callbacks.onResponseStart?.();
    });

    this.socket.on('response_chunk', (data: {chunk: string}) => {
      this.callbacks.onResponseChunk?.(data.chunk);
    });

    this.socket.on('response_complete', (data: {response: string; usage?: any}) => {
      this.callbacks.onResponseComplete?.(data.response, data.usage);
    });

    this.socket.on('error', (error: {message: string; error?: string}) => {
      this.callbacks.onError?.(error);
    });
  }

  sendMessage(message: string): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      return;
    }

    this.socket.emit('chat', {
      message
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export default new AIAssistantService();
